/**
 * A/B Testing module for Booster Landing Optimizer
 * Compatible with booster ecosystem test group storage format
 */
// Shared storage key across all booster tools
export const TEST_STORAGE_KEY = "booster_testgroup";
export const DEFAULT_APPLY_RATE = 0.5; // 50/50 split
export const DEFAULT_TEST_NAME = "landing_optimizer";
export function loadTestGroupStorage() {
    const storage = {};
    if (!window.localStorage) {
        return storage;
    }
    try {
        const serialized = localStorage.getItem(TEST_STORAGE_KEY);
        if (!serialized) {
            return storage;
        }
        const current = JSON.parse(serialized);
        // Validate each item in storage
        for (const key of Object.keys(current)) {
            const item = current[key];
            if (!item ||
                item.group == null ||
                item.applyRate == null ||
                item.updatedAt == null ||
                ![0, 1, 2, 3].includes(item.group)) {
                continue;
            }
            storage[key] = item;
        }
    }
    catch (error) {
        localStorage.removeItem(TEST_STORAGE_KEY);
    }
    return storage;
}
export function saveTestGroupStorage(storage) {
    if (!window.localStorage) {
        return;
    }
    try {
        localStorage.setItem(TEST_STORAGE_KEY, JSON.stringify(storage));
    }
    catch (error) {
        // Silent fail - localStorage may be unavailable
    }
}
export function updateTestGroupStorage(testName, group, applyRate) {
    if (!window.localStorage) {
        return;
    }
    const storage = loadTestGroupStorage();
    storage[testName] = {
        group,
        applyRate,
        updatedAt: Date.now()
    };
    saveTestGroupStorage(storage);
}
export function assignTestGroup(config) {
    const { testName, applyRate, forceGroup } = config;
    // Validate test name
    if (testName.includes(',') || testName.includes(':')) {
        throw new Error(`Invalid test name '${testName}'. It should not include ',' or ':'.`);
    }
    // Handle forced assignment (e.g., from URL parameter)
    if (forceGroup !== undefined) {
        updateTestGroupStorage(testName, forceGroup, applyRate);
        return forceGroup;
    }
    // Check for existing assignment with same apply rate
    if (window.localStorage) {
        const storage = loadTestGroupStorage();
        const existing = storage[testName];
        if (existing && existing.applyRate === applyRate) {
            return existing.group;
        }
    }
    // Generate new assignment using 4-group logic
    let group;
    // Handle edge cases: applyRate = 0 or 1
    if (applyRate === 0) {
        // 0% treatment rate → always control group with GA
        group = 0;
    }
    else if (applyRate === 1) {
        // 100% treatment rate → always treatment group with GA
        group = 1;
    }
    else {
        // Probabilistic assignment for intermediate values
        const rand = Math.random();
        // P(group=0) と P(group=1) の確率を決定する thres
        // これは applyRate と 1 - applyRate の小さい方になる
        const thres = Math.min(applyRate, 1 - applyRate);
        // 各グループの累積確率を計算
        const p0_cumulative_threshold = thres; // P(group=0)
        const p1_cumulative_threshold = p0_cumulative_threshold + thres; // P(group=0) + P(group=1)
        const p2_cumulative_threshold = p1_cumulative_threshold + (1 - applyRate - thres); // P(group=0) + P(group=1) + P(group=2)
        if (rand < p0_cumulative_threshold) {
            group = 0;
        }
        else if (rand < p1_cumulative_threshold) {
            group = 1;
        }
        else if (rand < p2_cumulative_threshold) {
            group = 2;
        }
        else {
            group = 3;
        }
    }
    // Store assignment
    updateTestGroupStorage(testName, group, applyRate);
    return group;
}
export function getTestGroup(testName) {
    if (!window.localStorage) {
        return null;
    }
    const storage = loadTestGroupStorage();
    const assignment = storage[testName];
    return assignment ? assignment.group : null;
}
export function parseApplyRateFromUrl(url) {
    const paramName = 'apply-rate';
    try {
        const urlObj = new URL(url);
        const applyRateParam = urlObj.searchParams.get(paramName);
        if (applyRateParam === null) {
            return null;
        }
        const parsedValue = Number.parseFloat(applyRateParam);
        if (Number.isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
            return null;
        }
        return parsedValue / 100; // Convert from 0-100 to 0-1
    }
    catch (error) {
        return null;
    }
}
export function parseForcedGroupFromUrl(url) {
    const paramName = 'force-group';
    try {
        const urlObj = new URL(url);
        const groupParam = urlObj.searchParams.get(paramName);
        if (groupParam === '0')
            return 0;
        if (groupParam === '1')
            return 1;
        if (groupParam === '2')
            return 2;
        if (groupParam === '3')
            return 3;
        return null;
    }
    catch (error) {
        return null;
    }
}
export function isStorageAvailable() {
    try {
        if (!window.localStorage) {
            return false;
        }
        // Test write/read/remove
        const testKey = '__booster_storage_test__';
        localStorage.setItem(testKey, 'test');
        const result = localStorage.getItem(testKey) === 'test';
        localStorage.removeItem(testKey);
        return result;
    }
    catch {
        return false;
    }
}
export function initializeABTesting(scriptUrl) {
    const isStorageAvailable = window.localStorage !== undefined;
    if (!isStorageAvailable) {
        return {
            testGroup: 0,
            config: {
                testName: DEFAULT_TEST_NAME,
                applyRate: DEFAULT_APPLY_RATE
            },
            isStorageAvailable: false
        };
    }
    // Parse configuration from script URL
    const currentUrl = scriptUrl || window.location.href;
    const applyRate = parseApplyRateFromUrl(currentUrl) ?? DEFAULT_APPLY_RATE;
    const forceGroup = parseForcedGroupFromUrl(currentUrl);
    const config = {
        testName: DEFAULT_TEST_NAME,
        applyRate,
        forceGroup: forceGroup ?? undefined
    };
    // Assign test group
    const testGroup = assignTestGroup(config);
    return {
        testGroup,
        config,
        isStorageAvailable: true
    };
}
//# sourceMappingURL=ab-testing.js.map